/**
 * API Service
 *
 * Axios instance configured with JWT token interceptor for authenticated API requests.
 * Automatically attaches ID token to Authorization header for API Gateway authorization.
 */

import axios from 'axios';
import { getIdToken, signOut } from '../auth';

// Get API Gateway URL from runtime configuration
const getBaseURL = () => {
  const endpoint = window.amplifyConfig?.API?.adminApi?.endpoint;
  if (!endpoint) {
    console.error('API Gateway endpoint not configured in window.amplifyConfig');
    return '';
  }
  return endpoint;
};

// Create axios instance with default configuration
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach JWT ID token to all requests
api.interceptors.request.use(
  async (config) => {
    try {
      const idToken = await getIdToken();

      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      } else {
        console.warn('No ID token available for API request');
      }

      return config;
    } catch (error) {
      console.error('Failed to attach ID token:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      console.error('API request unauthorized (401) - signing out');

      // Sign out and redirect to sign in
      try {
        await signOut();
      } catch (signOutError) {
        console.error('Failed to sign out:', signOutError);
        // Force redirect even if sign out fails
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Get admin configuration
 */
export const getAdminConfig = async () => {
  try {
    const response = await api.get('/admin/config');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch admin config:', error);
    throw error;
  }
};

/**
 * Update Bedrock prompt
 */
export const updatePrompt = async (promptId, promptData) => {
  try {
    const response = await api.post('/admin/prompts/update', {
      promptId,
      ...promptData,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update prompt:', error);
    throw error;
  }
};

/**
 * Validate Bedrock prompt ARN
 */
export const validatePromptArn = async (promptArn) => {
  try {
    const response = await api.post('/admin/prompts/validate', {
      promptArn,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to validate prompt ARN:', error);
    throw error;
  }
};

export default api;
